// Cloudflare Turnstile bot-protection helper -- renders an invisible
// widget on demand and resolves a token to send as `captchaToken` on
// /auth/login and /auth/register. Real production site key, paired with
// the secret key configured on the backend
// (https://dash.cloudflare.com/?to=/:account/turnstile).
export const TURNSTILE_SITE_KEY = "0x4AAAAAAE75PTpKcvEQ-EGZ";

// Pages that want the (normally invisible, zero-height) widget to sit in
// a specific spot -- e.g. right above the sign-in button, so an escalated
// interactive challenge appears exactly there instead of floating in a
// corner -- render `<div id={TURNSTILE_SLOT_ID} />` at that spot. Pages
// that don't provide one still get a working (if less ideally placed)
// widget via the fixed-corner fallback below.
export const TURNSTILE_SLOT_ID = "turnstile-slot";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, options: Record<string, unknown>) => string;
      execute: (widgetId: string, options?: Record<string, unknown>) => void;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;
let widgetId: string | null = null;
let widgetContainer: Element | null = null;
let pending: { resolve: (token: string) => void; reject: (err: Error) => void } | null = null;

function loadScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load Turnstile."));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

function getFallbackContainer(): HTMLElement {
  let el = document.getElementById("turnstile-invisible-container");
  if (el) return el;

  el = document.createElement("div");
  el.id = "turnstile-invisible-container";
  // Do NOT hide this with display:none or push it off-screen (e.g.
  // top: -9999px) -- appearance: "interaction-only" below already keeps
  // it reserving zero space and invisible when Cloudflare can verify
  // silently, but when it genuinely can't (a suspicious environment, a
  // stricter risk score, etc.) it renders an interactive challenge in
  // this exact element. An off-screen container makes that challenge
  // permanently impossible to see or complete, which is indistinguishable
  // from Turnstile being broken -- every affected visitor just times out.
  // Fixed to a corner instead, so it's out of the way normally but can
  // actually be reached if Cloudflare ever needs it. Only used when a
  // page doesn't provide its own TURNSTILE_SLOT_ID anchor.
  el.style.position = "fixed";
  el.style.bottom = "16px";
  el.style.right = "16px";
  el.style.zIndex = "9999";
  document.body.appendChild(el);
  return el;
}

function ensureWidget(): string {
  const target: HTMLElement =
    document.getElementById(TURNSTILE_SLOT_ID) ?? getFallbackContainer();

  // The widget is already live in the right place -- reuse it. Guards
  // against re-rendering on every call, and against reusing a widget
  // whose container got unmounted by client-side navigation (e.g.
  // /login -> /signup, each with their own slot element).
  if (widgetId !== null && widgetContainer === target && document.contains(target)) {
    return widgetId;
  }

  if (widgetId !== null) {
    try {
      window.turnstile!.remove(widgetId);
    } catch {
      // Already gone (container removed from the DOM) -- nothing to clean up.
    }
    widgetId = null;
  }

  widgetContainer = target;
  widgetId = window.turnstile!.render(target, {
    sitekey: TURNSTILE_SITE_KEY,
    // "invisible" is not a valid `size` value (Cloudflare only accepts
    // compact/flexible/normal there) -- every render() call was throwing
    // a TurnstileError synchronously and never actually initializing the
    // widget, which is why verification failed identically everywhere
    // regardless of domain/key config. `appearance: "interaction-only"`
    // is the real mechanism for an invisible-until-needed challenge: the
    // widget stays hidden and reserves no space unless Cloudflare
    // actually needs to show an interactive challenge.
    appearance: "interaction-only",
    execution: "execute",
    callback: (token: string) => {
      pending?.resolve(token);
      pending = null;
    },
    "error-callback": () => {
      pending?.reject(new Error("Verification challenge failed. Please try again."));
      pending = null;
    },
    "timeout-callback": () => {
      pending?.reject(new Error("Verification challenge timed out. Please try again."));
      pending = null;
    },
  });
  return widgetId;
}

/**
 * Resolves with a fresh Turnstile token. Tokens are single-use, so this
 * re-executes the (already-rendered) invisible widget on every call.
 * No-ops with an empty string on the server (SSR) -- callers only ever
 * invoke this from a form submit handler, which is client-only anyway.
 *
 * Also no-ops outside production. This site key is restricted to the
 * real domains, so Cloudflare's widget can't complete a challenge from
 * localhost at all -- disabling verification on the backend alone
 * (BUILD=dev / DISABLE_TURNSTILE) doesn't help, since this function is
 * awaited BEFORE the request that backend check guards ever fires; a
 * login/register submit would just hang or reject here and never reach
 * the backend. Skipping here matches the backend's BUILD=dev bypass,
 * which likewise doesn't check the token value it's sent.
 */
export async function getTurnstileToken(): Promise<string> {
  if (typeof window === "undefined") return "";
  if (process.env.NODE_ENV !== "production") return "dev-bypass";
  await loadScript();
  const id = ensureWidget();

  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (action: () => void) => {
      if (settled) return;
      settled = true;
      action();
    };

    // Belt-and-suspenders on top of Turnstile's own timeout-callback --
    // a widget that never calls back at all (script hiccup, browser
    // extension interference) must not be able to spin the caller's
    // submit button forever.
    const timer = setTimeout(() => {
      settle(() => reject(new Error("Verification challenge timed out. Please try again.")));
    }, 20000);

    pending = {
      resolve: (token) => {
        clearTimeout(timer);
        settle(() => resolve(token));
      },
      reject: (err) => {
        clearTimeout(timer);
        settle(() => reject(err));
      },
    };
    window.turnstile!.reset(id);
    window.turnstile!.execute(id);
  });
}
